// App.js - Main application component
import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import GameBoard from './components/GameBoard';

function App() {
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [difficulty, setDifficulty] = useState('medium'); // easy, medium, hard
  const [showSettings, setShowSettings] = useState(false);
  const [cardRevealTime, setCardRevealTime] = useState(500); // Default 500ms
  const [clickToContinue, setClickToContinue] = useState(true); // Click to continue - ON by default

  // Automatically start a medium difficulty game on component mount
  useEffect(() => {
    setGameStarted(true);
  }, []);

  // Path to card images - UPDATED to use local images folder
  const cardImagePath = '/images/';
  const cardBack = cardImagePath + 'back and 3x.png';

  // Special cards definitions
  const specialCards = [
    { id: 'crow', name: 'Crows x2.png', value: 100 },
    { id: 'mischief', name: 'Mischief_x10.png', value: 101 },
    { id: 'poop', name: 'Poop x2.png', value: 102 },
    { id: 'robin', name: 'Robin x2.png', value: 103 },
  ];

  // Prepare card data based on difficulty level
  useEffect(() => {
    if (!gameStarted) return;

    let selectedCards = [];
    const suits = ['g', 'dih', 'knot', 'r'];
    const difficultySettings = {
      easy: { pairs: 8, includeSpecial: 1 },
      medium: { pairs: 12, includeSpecial: 2 },
      hard: { pairs: 20, includeSpecial: 4 },
    };

    const { pairs, includeSpecial } = difficultySettings[difficulty];
    const regularPairs = pairs - includeSpecial;

    // Create array of all available cards including face cards
    const potentialCards = [];

    // Add number cards (0-9)
    for (let num = 0; num <= 9; num++) {
      for (let i = 0; i < suits.length; i++) {
        const suit = suits[i];
        potentialCards.push({
          id: `${num}${suit}`,
          number: num,
          suit: suit,
          front: getCardImage(num, suit),
          back: cardBack,
          matched: false,
          flipped: false,
        });
      }
    }

    // Add face cards (Jay, Pecker, Raptor)
    const faceCards = ['J', 'P', 'R'];
    for (let face of faceCards) {
      for (let suit of suits) {
        potentialCards.push({
          id: `${face}${suit}`,
          number: face === 'J' ? 10 : face === 'P' ? 11 : 12, // Numeric value for sorting/logic
          suit: suit,
          faceCard: face,
          front: `${cardImagePath}${face}${suit}.png`,
          back: cardBack,
          matched: false,
          flipped: false,
        });
      }
    }

    // Add special cards
    const specialCardObjects = specialCards.map((special) => ({
      id: special.id,
      number: special.value,
      special: true,
      front: `${cardImagePath}${special.name}`,
      back: cardBack,
      matched: false,
      flipped: false,
    }));

    // Perform an initial true shuffle of all cards
    const shuffledPotential = [...potentialCards];
    for (let i = shuffledPotential.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledPotential[i], shuffledPotential[j]] = [
        shuffledPotential[j],
        shuffledPotential[i],
      ];
    }

    // Shuffle special cards too
    const shuffledSpecials = [...specialCardObjects];
    for (let i = shuffledSpecials.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledSpecials[i], shuffledSpecials[j]] = [
        shuffledSpecials[j],
        shuffledSpecials[i],
      ];
    }

    // Select special cards based on difficulty
    const selectedSpecialCards = shuffledSpecials.slice(0, includeSpecial);

    // Select a mix of regular cards
    const selectedFaceCards = shuffledPotential.filter((card) => card.faceCard);
    const selectedNumberCards = shuffledPotential.filter(
      (card) => !card.faceCard
    );

    // Ensure at least some face cards are included when possible
    let facePairs = Math.min(
      Math.floor(regularPairs * 0.3),
      selectedFaceCards.length
    ); // ~30% face cards
    let numberPairs = regularPairs - facePairs;

    // Get random face cards
    const randomFaceCards = selectedFaceCards.slice(0, facePairs);

    // Get random number cards
    const randomNumberCards = selectedNumberCards.slice(0, numberPairs);

    // Combine selections
    selectedCards = [
      ...selectedSpecialCards,
      ...randomFaceCards,
      ...randomNumberCards,
    ];

    // If we still need more cards (e.g., if we didn't have enough face cards)
    if (selectedCards.length < pairs) {
      const remaining = pairs - selectedCards.length;
      const remainingCards = shuffledPotential
        .filter(
          (card) =>
            !selectedCards.some(
              (selected) =>
                selected.id === card.id ||
                (selected.number === card.number && selected.suit === card.suit)
            )
        )
        .slice(0, remaining);

      selectedCards = [...selectedCards, ...remainingCards];
    }

    // Create pairs by duplicating selected cards
    let pairedCards = [];
    selectedCards.forEach((card) => {
      // Create two instances of each card for matching
      pairedCards.push({ ...card, id: `${card.id}-1` });
      pairedCards.push({ ...card, id: `${card.id}-2` });
    });

    // Improved shuffle algorithm (Fisher-Yates) for better randomization
    const shuffledCards = [...pairedCards];
    for (let i = shuffledCards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledCards[i], shuffledCards[j]] = [
        shuffledCards[j],
        shuffledCards[i],
      ];
    }

    // Extra shuffle to prevent pairs from appearing adjacent
    const secondShuffle = [...shuffledCards];
    for (let i = secondShuffle.length - 1; i > 0; i--) {
      // Get a random index that's far from current index when possible
      let j =
        (i + Math.floor(Math.random() * (secondShuffle.length / 2)) + 3) %
        secondShuffle.length;
      [secondShuffle[i], secondShuffle[j]] = [
        secondShuffle[j],
        secondShuffle[i],
      ];
    }

    setCards(secondShuffle);
    setFlippedCards([]);
    setMatchedPairs([]);
    setMoves(0);
    setGameComplete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted, difficulty, cardImagePath, cardBack]);

  // Function to get the correct image path for each card
  const getCardImage = (number, suit) => {
    if (number >= 0 && number <= 9) {
      // Handle number cards - using correct filenames without spaces
      return `${cardImagePath}${number}${suit}.png`;
    } else if (number === 10) {
      // Jay cards
      return `${cardImagePath}J${suit}.png`;
    } else if (number === 11) {
      // Pecker cards
      return `${cardImagePath}P${suit}.png`;
    } else if (number === 12) {
      // Raptor cards
      return `${cardImagePath}R${suit}.png`;
    }
    return cardBack; // Default fallback
  };

  // Handle card clicks
  const handleCardClick = (cardId) => {
    // Prevent actions if game isn't started
    if (!gameStarted || gameComplete) return;

    // If click-to-continue is enabled and we have two non-matching cards flipped,
    // clicking any card should hide the flipped cards AND then process the new card click
    if (clickToContinue && flippedCards.length === 2) {
      // Find the clicked card
      const clickedCard = cards.find((card) => card.id === cardId);

      // Only proceed if we're clicking on a new valid card
      if (
        clickedCard &&
        !clickedCard.matched &&
        !flippedCards.includes(cardId)
      ) {
        // Reset flipped cards and then process the new card after a short delay
        setFlippedCards([]);

        // Small delay to show the cards flipping back before processing the new click
        setTimeout(() => {
          // Process the new card click after the previous cards are hidden
          setFlippedCards([cardId]);
        }, 100);
      } else {
        // Just hide the current cards if clicking on the same card or an invalid target
        setFlippedCards([]);
      }
      return;
    }

    // Find the clicked card
    const clickedCard = cards.find((card) => card.id === cardId);

    // Prevent clicking on matched cards or already flipped cards
    if (
      !clickedCard ||
      clickedCard.matched ||
      flippedCards.includes(cardId) ||
      flippedCards.length >= 2
    ) {
      return;
    }

    // Add card to flipped cards
    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    // Check for matches when two cards are flipped
    if (newFlippedCards.length === 2) {
      setMoves((prevMoves) => prevMoves + 1);

      const firstCard = cards.find((card) => card.id === newFlippedCards[0]);
      const secondCard = cards.find((card) => card.id === newFlippedCards[1]);

      // Cards match if they have the same number and suit (regular cards),
      // or if they're both the same special card
      const isMatch =
        (firstCard.special &&
          secondCard.special &&
          firstCard.number === secondCard.number) ||
        (!firstCard.special &&
          !secondCard.special &&
          firstCard.number === secondCard.number &&
          firstCard.suit === secondCard.suit);

      if (isMatch) {
        // Create unique identifier for the matched pair
        const matchId = firstCard.special
          ? firstCard.id
          : `${firstCard.number}${firstCard.suit}`;
        const newMatchedPairs = [...matchedPairs, matchId];
        setMatchedPairs(newMatchedPairs);

        // Update cards as matched
        const updatedCards = cards.map((card) => {
          if (card.id === firstCard.id || card.id === secondCard.id) {
            return { ...card, matched: true };
          }
          return card;
        });
        setCards(updatedCards);

        // Check if game is complete
        if (newMatchedPairs.length === cards.length / 2) {
          setGameComplete(true);
        }

        // Reset flipped cards immediately for matches
        setFlippedCards([]);
      } else if (!clickToContinue) {
        // If no match and NOT using click-to-continue, flip them back after a delay
        setTimeout(() => {
          setFlippedCards([]);
        }, cardRevealTime);
      }
      // If using click-to-continue, we don't set a timeout - user must click to continue
    }
  };

  // Start a new game
  const startNewGame = () => {
    setGameStarted(true);
  };

  // Change difficulty level
  const changeDifficulty = (level) => {
    setDifficulty(level);
    if (gameStarted) {
      setGameStarted(false);
      setTimeout(() => {
        setGameStarted(true);
      }, 100);
    }
  };

  // Toggle settings panel
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  return (
    <div className="app">
      <Header
        moves={moves}
        gameComplete={gameComplete}
        startNewGame={startNewGame}
        gameStarted={gameStarted}
        difficulty={difficulty}
        changeDifficulty={changeDifficulty}
        toggleSettings={toggleSettings}
      />

      {showSettings && (
        <div className="settings-panel">
          <h3>Settings</h3>

          <div className="setting-row">
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="clickToContinue"
                checked={clickToContinue}
                onChange={() => setClickToContinue(!clickToContinue)}
              />
              <label htmlFor="clickToContinue">Click to Continue Mode</label>
              <p className="setting-description"></p>
            </div>
          </div>

          <div className="setting-row">
            <label htmlFor="cardRevealTime">
              Card Reveal Time: {cardRevealTime}ms
              <span className={clickToContinue ? 'disabled-text' : ''}>
                {clickToContinue ? ' (Not used in Click to Continue mode)' : ''}
              </span>
            </label>
            <div
              className={`slider-container ${
                clickToContinue ? 'disabled' : ''
              }`}
            >
              <span>Fast</span>
              <input
                type="range"
                id="cardRevealTime"
                min="200"
                max="2000"
                step="100"
                value={cardRevealTime}
                onChange={(e) => setCardRevealTime(parseInt(e.target.value))}
                disabled={clickToContinue}
              />
              <span>Slow</span>
            </div>
            <div
              className={`preset-buttons ${clickToContinue ? 'disabled' : ''}`}
            >
              <button
                onClick={() => setCardRevealTime(300)}
                disabled={clickToContinue}
              >
                Fast
              </button>
              <button
                onClick={() => setCardRevealTime(800)}
                disabled={clickToContinue}
              >
                Medium
              </button>
              <button
                onClick={() => setCardRevealTime(1500)}
                disabled={clickToContinue}
              >
                Slow
              </button>
            </div>
          </div>

          <button className="close-settings" onClick={toggleSettings}>
            Close
          </button>
        </div>
      )}

      <GameBoard
        cards={cards}
        flippedCards={flippedCards}
        handleCardClick={handleCardClick}
        gameStarted={gameStarted}
        clickToContinue={clickToContinue}
      />

      <footer className="game-footer">
        <div className="credit">
          <a
            href="https://fractalkitty.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/images/cropped.png"
              alt="FractalKitty Logo"
              className="footer-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='50'><text x='10' y='30' font-family='Arial' font-size='20' fill='%238e44ad'>FractalKitty</text></svg>";
              }}
            />
            <span></span>
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;
